/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Col, Row, message } from 'antd';
import { RetweetOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import { Form, Select, Input } from 'formik-antd';
import moment from 'moment';
import * as Yup from 'yup';
import useAuth from 'hooks/useAuth';
import { Box, FormInput, CustomInput, Button } from 'atoms';
import { DatePickers } from 'utils/commonStyled';
import { ACL, EditModeBtn } from 'components/common';
import ReplaceVehicleModal from 'pagesv2/vehiclesv2/edit-vehiclev2/ReplaceVehicleModal';
import ReplaceDriverModal from 'pagesv2/vehiclesv2/edit-vehiclev2/ReplaceDriverModal';
import VehicleSelectionFromAnotherVendorModal from 'pagesv2/vehiclesv2/edit-vehiclev2/VehicleSelectionFromAnotherVendorModal';
import UploadWithPreview from '../common/UploadWithPreview';
import AddressPickerV2 from 'components/common/AddressPickerV2/AddressPickerV2';
import loadGoogleMapsV2 from 'utils/loadGoogleMapsV2';
import { truncateAddressToCity } from 'utils/addressHelpers';
import {
	VEHICLE_REPLACEMENT_STATUS_IDS,
	DRIVER_REPLACEMENT_STATUS_IDS,
	DRIVER_DROPDOWN_EDITABLE_STATUS_IDS,
	DRIVER_ASSIGN_WHEN_UNASSIGNED_STATUS_IDS,
} from 'utils/tripStatus';
import NodeSequenceModal from 'components/common/NodeSequenceModal/NodeSequenceModal';

const RouteContainer = styled.div`
	margin-bottom: 10px;
`;
const RouteFieldsRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;
const RouteFieldWrapper = styled.div`
	flex: 1;
	min-width: 0;
`;
const StopsIndicator = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	color: #888;
	font-size: 13px;
	white-space: nowrap;
	padding-top: 28px;
`;
const SelectNodeLink = styled.button`
	border: none;
	background: transparent;
	color: #1677ff;
	font-size: 14px;
	font-weight: 500;
	padding: 0;
	cursor: pointer;
	display: block;
	text-align: right;
	text-decoration: underline;
	margin-top: 4px;
	margin-left: auto;

	&:hover {
		text-decoration: underline;
	}

	&:disabled {
		color: #94a3b8;
		cursor: not-allowed;
		text-decoration: none;
	}
`;

const normalizeAddress = (address = '') => address.trim().toLowerCase();

const nodeListAfterRoundTripFromStart = (prevNodes, startAddress) => {
	const startAddr = startAddress || '';
	if (!prevNodes?.length) {
		return [
			{ address: startAddr, type: 'start' },
			{ address: startAddr, type: 'end' },
		];
	}
	const next = [...prevNodes];
	if (next[0]) {
		next[0] = { ...next[0], address: startAddr };
	}
	next[next.length - 1] = {
		...next[next.length - 1],
		address: startAddr,
	};
	return next;
};

const shouldValidateSameAddressForOneWay = (startAddress, destinationAddress) =>
	Boolean(
		startAddress &&
			destinationAddress &&
			normalizeAddress(startAddress) === normalizeAddress(destinationAddress)
	);

const FormContainer = styled(Box)`
	max-width: 1200px;
	margin: 32px auto;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
	padding: 40px 60px 32px 60px;
`;
const FormButtonContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 18px;
`;
const FormPrimaryButton = styled(Button)`
	background: #18405d;
	color: #fff;
	border-radius: 4px;
	padding: 7px 24px;
	font-size: 15px;
	font-weight: 500;
`;
const FormCancelButton = styled(Button)`
	background: #fff;
	color: #18405d;
	border: 1px solid #18405d;
	border-radius: 4px;
	padding: 7px 24px;
	font-size: 15px;
	font-weight: 500;
`;
const CompactRow = styled(Row)`
	margin-bottom: 10px !important;
`;
const CompactCol = styled(Col)`
	margin-bottom: 0 !important;
`;
const ReplaceLinkRow = styled.div`
	display: flex;
	justify-content: flex-end;
	margin: 4px 0 8px;
`;
const ReplaceLink = styled.button`
	border: none;
	background: none;
	color: #1890ff;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 0;

	&:hover {
		color: #40a9ff;
		text-decoration: underline;
	}

	&:disabled {
		color: #94a3b8;
		cursor: not-allowed;
		text-decoration: none;
	}
`;
// LocationFieldRow, PincodeInput, CityStateInput removed as they are not used

// Helper functions to reduce cognitive complexity
const isValidDate = (value) => value && (value instanceof Date || moment(value).isValid());

const isPositiveNumber = (value) => {
	if (!value) return false;
	const num = Number(value);
	return !Number.isNaN(num) && num > 0;
};

const isNonZeroNumber = (value) => {
	if (!value) return false;
	return Number(value) !== 0;
};

const createDateValidation = (fieldName) =>
	Yup.mixed()
		.required(`${fieldName} is required`)
		.test('is-valid-date', `Invalid ${fieldName.toLowerCase()}`, isValidDate)
		.label(fieldName);

const createNumberValidation = (fieldName, allowZero = false) => {
	const schema = Yup.string()
		.required()
		.test('is-number', `${fieldName} must be a number`, isPositiveNumber);
	if (!allowZero) {
		return schema.test('not-zero', `${fieldName} should not be zero`, isNonZeroNumber);
	}
	return schema;
};

const createAdhocField = (schema) =>
	Yup.string().when('trip_type', {
		is: 'adhoc',
		then: schema,
		otherwise: Yup.string().optional(),
	});

const createRegularField = (schema) =>
	Yup.string().when('trip_type', {
		is: 'regular',
		then: schema,
		otherwise: Yup.string().optional(),
	});

// Validation schema for IntracityTripForm
const validationSchema = (isV2UI = false) =>
	Yup.object({
		trip_type: Yup.string().required().label('Trip Type'),
		city: Yup.string().required().label('City'),
		project_id: Yup.string().required().label('Project'),
		route_id: isV2UI
			? createRegularField(Yup.string().required().label('Route'))
			: Yup.string().optional(),
		vendor_id: Yup.string().required().label('Vendor'),
		vendor_type: createAdhocField(Yup.string().required().label('Vendor Type')),
		vehicle_number: isV2UI
			? Yup.string().required().label('Vehicle No')
			: Yup.string()
					.min(8)
					.max(12)
					.matches(
						/^[A-Z]{2}[A-Z|0-9]{1,3}[A-Z|0-9]{0,3}[0-9]{4}$/,
						'Enter Valid Vehicle Number'
					)
					.required()
					.label('Vehicle Number'),
		reporting_date: createDateValidation('Reporting Date'),
		start_date: createDateValidation('Start Date').test(
			'start-after-reporting',
			'Start Date cannot be before Reporting Date',
			function (value) {
				const { reporting_date } = this.parent;
				if (!value || !reporting_date) return true;
				return moment(value).isSameOrAfter(moment(reporting_date));
			}
		),
		vehicle_type: Yup.string().required().label('Vehicle Type'),
		start_km: createNumberValidation('Start KM').label('Start KM'),
		driver_id: isV2UI
			? Yup.string().required().label('Driver')
			: createRegularField(Yup.string().required().label('Driver')),
		driver_name: isV2UI
			? Yup.string().optional()
			: createAdhocField(Yup.string().required().label('Driver Name')),
		driver_number: isV2UI
			? Yup.string().optional()
			: createAdhocField(
					Yup.string()
						.required('Driver number is required')
						.matches(/^[6-9]/, 'Driver number must start with 6, 7, 8, or 9')
						.matches(
							/^[6-9][0-9]{9}$/,
							'Driver number must be exactly 10 digits starting with 6, 7, 8, or 9'
						)
						.test('max-length', 'Driver number cannot exceed 10 digits', (value) =>
							value ? value.length <= 10 : true
						)
			  ),
		client_rate: createAdhocField(
			Yup.string()
				.required()
				.test('is-number', 'Client rate must be a number', isPositiveNumber)
				.test(
					'is-greater',
					'Client rate must be greater than vendor rate',
					function (value) {
						const { vendor_rate } = this.parent;
						if (!value || !vendor_rate) return false;
						return Number(value) > Number(vendor_rate);
					}
				)
				.label('Client Rate')
		),
		vendor_rate: createAdhocField(
			Yup.string()
				.required()
				.test('is-number', 'Vendor rate must be a number', isPositiveNumber)
				.label('Vendor Rate')
		),
		requirement_proof: Yup.array()
			.when('trip_type', {
				is: 'adhoc',
				then: Yup.array().when('isEditModeFlag', {
					is: true,
					then: (schema) => schema.optional(),
					otherwise: (schema) =>
						schema.test('has-files', 'Proof of request is required', function (value) {
							return value && Array.isArray(value) && value.length > 0;
						}),
				}),
				otherwise: Yup.array().optional(),
			})
			.label('Proof of Request'),
		...(isV2UI
			? {
					route_type: createAdhocField(
						Yup.string().required('Route type is required').label('Route Type')
					),
					start_address: createAdhocField(
						Yup.string()
							.required('Start address is required')
							.test(
								'different-from-destination',
								'Start and destination address cannot be same',
								function (value) {
									const { destination_address, route_type } = this.parent || {};
									if (route_type === 'round_trip') return true;
									if (!value || !destination_address) return true;
									return (
										normalizeAddress(value) !==
										normalizeAddress(destination_address)
									);
								}
							)
							.label('Start Address')
					),
					destination_address: createAdhocField(
						Yup.string()
							.required('Destination address is required')
							.test(
								'different-from-source',
								'Source and destination address cannot be same',
								function (value) {
									const { start_address, route_type } = this.parent || {};
									if (route_type === 'round_trip') return true;
									if (!value || !start_address) return true;
									return (
										normalizeAddress(value) !== normalizeAddress(start_address)
									);
								}
							)
							.label('Destination Address')
					),
			  }
			: {}),
		remarks: Yup.string().max(250, 'Remarks cannot exceed 250 characters').label('Remarks'),
	});

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function IntracityTripForm({
	isEditMode = false,
	formData,
	cities,
	projectData = [],
	vendorsData = [],
	vehicleTypes = [],
	vehicleTypeIdMap = {},
	vehicleData = [],
	driverData = [],
	routeData = [], // Routes for v2 API
	isV2UI = false, // Flag to show v2-only fields
	uploadingOdometer,
	uploadErrorOdometer,
	uploadingProof,
	uploadErrorProof,
	vendorUserType = [],
	formRef,
	onCityChange,
	onProjectChange,
	onVendorTypeChange,
	onVehicleTypeChange,
	onVendorChange,
	onTripTypeChange,
	onRouteChange,
	onVehicleNumberChange,
	clientAllowsCrossVendor = false,
	onCrossVendorSelect,
	onDriverChange,
	onProofUpload,
	onOdometerUpload,
	onSubmit,
	navigate,
	source_entry = '',
}) {
	const initialValues = React.useMemo(
		// eslint-disable-next-line sonarjs/cognitive-complexity
		() => ({
			trip_type: formData?.trip_type || 'adhoc',
			city: formData?.city || '',
			project_id: formData?.project_id || '', // use ID
			route_id: formData?.route_id || '', // Route ID for v2 API
			route_type: formData?.route_type,
			vendor_id: formData?.vendor_id || '', // use ID
			vendor_type: formData?.vendor_type || '',
			vehicle_number: formData?.vehicle_number || '',
			reporting_date: formData?.reporting_date || undefined,
			start_date: formData?.start_date || undefined,
			end_date: formData?.end_date || undefined,
			start_address: formData?.start_address || '',
			destination_address: formData?.destination_address || '',
			intermediary_addresses: formData?.intermediary_addresses || [],
			node_sequence: formData?.node_sequence || [],
			start_location_pincode: formData?.start_location_pincode || '',
			end_location_pincode: formData?.end_location_pincode || '',
			vehicle_type: formData?.vehicle_type || '',
			driver_id: formData?.driver_id || '',
			driver_name: formData?.driver_name || '',
			driver_number: formData?.driver_number || '',
			odometer_start: formData?.odometer_start || [],
			client_rate: formData?.client_rate || '',
			vendor_rate: formData?.vendor_rate || '',
			start_km: formData?.start_km || '',
			adhoc_reason: 'Extra Requirement',
			requirement_proof: formData?.requirement_proof || [],
			isEditModeFlag: isEditMode,
			remarks: formData?.remarks || '',
		}),
		[formData, isEditMode]
	);

	const { permissions, v2_permissions } = useAuth();
	const [isFormEditable, setIsFormEditable] = React.useState(false);
	const [nodeSequenceModalVisible, setNodeSequenceModalVisible] = React.useState(false);
	const [nodeSequence, setNodeSequence] = React.useState([]);
	const [replaceVehicleVisible, setReplaceVehicleVisible] = React.useState(false);
	const [replaceDriverVisible, setReplaceDriverVisible] = React.useState(false);
	const [crossVendorVisible, setCrossVendorVisible] = React.useState(false);
	const showReplaceActions = isEditMode && isV2UI && !formData?.isEnded;
	const currentStatusId = Number(formData?.trip_status_id);
	const hasNoDriverAssigned = !formData?.driver_id;
	const updatePermissions = isV2UI ? v2_permissions?.update : permissions?.update;
	const hasReplaceVehiclePermission =
		updatePermissions?.includes('trip_replace_vehicle') || updatePermissions?.includes('all');
	const hasReplaceDriverPermission =
		updatePermissions?.includes('trip_replace_driver') || updatePermissions?.includes('all');
	const canReplaceVehicle =
		showReplaceActions &&
		hasReplaceVehiclePermission &&
		VEHICLE_REPLACEMENT_STATUS_IDS.includes(currentStatusId);
	const canReplaceDriver =
		showReplaceActions &&
		hasReplaceDriverPermission &&
		!hasNoDriverAssigned &&
		DRIVER_REPLACEMENT_STATUS_IDS.includes(currentStatusId);
	const isDriverStatusEditable = DRIVER_DROPDOWN_EDITABLE_STATUS_IDS.includes(currentStatusId);
	// Eligible clients can borrow a vehicle from another vendor — at trip CREATION only.
	const canSelectCrossVendorVehicle = isV2UI && !isEditMode && !!clientAllowsCrossVendor;
	const canAssignDriverForLaterStatus =
		isV2UI &&
		isEditMode &&
		isFormEditable &&
		hasNoDriverAssigned &&
		DRIVER_ASSIGN_WHEN_UNASSIGNED_STATUS_IDS.includes(currentStatusId);

	React.useEffect(() => {
		if (isV2UI) {
			loadGoogleMapsV2().catch((err) => {
				console.error('Failed to load Google Maps:', err);
			});
		}
	}, [isV2UI]);

	React.useEffect(() => {
		if (!isV2UI) {
			setNodeSequence([]);
			return;
		}

		if (Array.isArray(formData?.node_sequence) && formData.node_sequence.length > 0) {
			setNodeSequence(formData.node_sequence);
			return;
		}

		const startNode = formData?.start_address
			? [{ address: formData.start_address, type: 'start' }]
			: [];
		const intermediaryNodes = Array.isArray(formData?.intermediary_addresses)
			? formData.intermediary_addresses
					.filter(Boolean)
					.map((address) => ({ address, type: 'intermediary' }))
			: [];
		const endNode = formData?.destination_address
			? [{ address: formData.destination_address, type: 'end' }]
			: [];

		setNodeSequence([...startNode, ...intermediaryNodes, ...endNode]);
	}, [formData, isV2UI]);

	return (
		<FormContainer>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema(isV2UI)}
				onSubmit={onSubmit}
				enableReinitialize
				validateOnChange
				validateOnBlur
				innerRef={formRef}
			>
				{/* eslint-disable-next-line sonarjs/cognitive-complexity */}
				{({ values, setFieldValue, isSubmitting, setFieldTouched, validateForm }) => (
					<>
						<ACL action="update" module="trip">
							{isEditMode && !formData?.isEnded && (
								<Row gutter={16} justify="space-between">
									<div></div>
									<EditModeBtn
										label={isFormEditable ? 'Cancel Edit' : 'Edit'}
										handleEdit={() => setIsFormEditable((prev) => !prev)}
									/>
								</Row>
							)}
						</ACL>
						<Form className="" disabled={isEditMode && !isFormEditable}>
							{/* Replicate screenshot field order and grouping */}
							<CompactRow gutter={16}>
								<CompactCol span={12}>
									<FormInput name="trip_type" label="Trip Type *">
										<Select
											name="trip_type"
											options={[
												{ value: 'regular', label: 'Regular' },
												{ value: 'adhoc', label: 'Adhoc' },
											]}
											disabled={isEditMode}
											onChange={(e) => {
												setFieldValue('trip_type', e);
												onTripTypeChange(e, setFieldValue, values);
											}}
										/>
									</FormInput>
								</CompactCol>
								<CompactCol span={12}>
									<FormInput name="city" label="City *">
										<Select
											name="city"
											showSearch
											options={cities}
											placeholder="Select"
											disabled={isEditMode}
											onChange={(e) => onCityChange(e, setFieldValue)}
										/>
									</FormInput>
								</CompactCol>
							</CompactRow>
							<CompactRow gutter={16}>
								<CompactCol span={12}>
									<FormInput name="project_id" label="Select Project *">
										<Select
											name="project_id"
											showSearch
											options={projectData}
											placeholder="Select"
											disabled={isEditMode}
											onChange={(e) => {
												setFieldValue('project_id', e);
												onProjectChange(e, setFieldValue, values);
											}}
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
										/>
									</FormInput>
								</CompactCol>
								{isV2UI && values.trip_type === 'adhoc' && (
									<CompactCol span={12}>
										<FormInput name="route_type" label="Route Type*">
											<Select
												name="route_type"
												options={[
													{ value: 'one_way', label: 'One Way' },
													{ value: 'round_trip', label: 'Round Trip' },
												]}
												placeholder="Select"
												disabled={isEditMode}
												onChange={async (routeType) => {
													if (!routeType) return;
													await setFieldValue('route_type', routeType);
													if (routeType === 'round_trip') {
														const startAddr =
															values.start_address || '';
														await setFieldValue(
															'destination_address',
															startAddr
														);
														await setFieldValue(
															'end_location_pincode',
															values.start_location_pincode || ''
														);
														const nextNodes =
															nodeListAfterRoundTripFromStart(
																nodeSequence,
																startAddr
															);
														setNodeSequence(nextNodes);
														await setFieldValue(
															'intermediary_addresses',
															nextNodes
																.filter(
																	(n) =>
																		n.type === 'intermediary' &&
																		n.address
																)
																.map((n) => n.address)
														);
														return;
													}
													// Switching away from round_trip — clear the auto-filled destination
													await setFieldValue('destination_address', '');
													await setFieldValue('end_location_pincode', '');
													setNodeSequence((prev) => {
														if (prev.length === 0) return prev;
														const updated = [...prev];
														updated[updated.length - 1] = {
															...updated[updated.length - 1],
															address: '',
														};
														return updated;
													});
												}}
											/>
										</FormInput>
									</CompactCol>
								)}
								{isV2UI && values.trip_type !== 'adhoc' && (
									<CompactCol span={12}>
										<FormInput name="route_id" label="Select Route *">
											<Select
												name="route_id"
												showSearch
												options={routeData}
												placeholder="Select"
												disabled={isEditMode}
												onChange={(e) =>
													onRouteChange(e, setFieldValue, values)
												}
												filterOption={(input, option) =>
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
											/>
										</FormInput>
									</CompactCol>
								)}
								{!isV2UI && values.trip_type === 'adhoc' && (
									<CompactCol span={12}>
										<FormInput name="adhoc_reason" label="Adhoc Reason *">
											<CustomInput
												name="adhoc_reason"
												value="Extra Requirement"
												disabled
											/>
										</FormInput>
									</CompactCol>
								)}
							</CompactRow>
							{isV2UI && values.trip_type === 'adhoc' && (
								<>
									<CompactRow gutter={16}>
										<CompactCol span={12}>
											<RouteContainer>
												<div style={{ fontWeight: 500, marginBottom: 8 }}>
													Route
												</div>
												<RouteFieldsRow>
													<RouteFieldWrapper>
														<FormInput name="start_address" label=" ">
															<AddressPickerV2
																value={values.start_address || ''}
																onChange={(data) => {
																	const truncatedAddress =
																		truncateAddressToCity(
																			data.address,
																			data.city || ''
																		);
																	const isRoundTrip =
																		values.route_type ===
																		'round_trip';
																	setFieldValue(
																		'start_address',
																		truncatedAddress
																	);
																	setFieldValue(
																		'start_location_pincode',
																		data.pincode ||
																			values.start_location_pincode
																	);
																	if (isRoundTrip) {
																		setFieldValue(
																			'destination_address',
																			truncatedAddress
																		);
																		setFieldValue(
																			'end_location_pincode',
																			data.pincode ||
																				values.end_location_pincode
																		);
																	}
																	setNodeSequence((prev) => {
																		const u = [...prev];
																		if (u.length > 0) {
																			u[0] = {
																				...u[0],
																				address:
																					truncatedAddress,
																			};
																			if (isRoundTrip)
																				u[u.length - 1] = {
																					...u[
																						u.length - 1
																					],
																					address:
																						truncatedAddress,
																				};
																			return u;
																		}
																		return [
																			{
																				address:
																					truncatedAddress,
																				type: 'start',
																			},
																			{
																				address: isRoundTrip
																					? truncatedAddress
																					: values.destination_address ||
																					  '',
																				type: 'end',
																			},
																		];
																	});
																	setTimeout(
																		() => validateForm(),
																		0
																	);
																}}
																placeholder="Select Source"
																disabled={
																	isEditMode || !values.route_type
																}
															/>
														</FormInput>
													</RouteFieldWrapper>
													{nodeSequence.filter(
														(n) =>
															n.type === 'intermediary' && n.address
													).length > 0 ? (
														<StopsIndicator>
															{'··· '}
															{
																nodeSequence.filter(
																	(n) =>
																		n.type === 'intermediary' &&
																		n.address
																).length
															}
															{' stop'}
															{nodeSequence.filter(
																(n) =>
																	n.type === 'intermediary' &&
																	n.address
															).length > 1
																? 's'
																: ''}
															{' ···>'}
														</StopsIndicator>
													) : null}
													<RouteFieldWrapper>
														<FormInput
															name="destination_address"
															label=" "
														>
															<AddressPickerV2
																value={
																	values.destination_address || ''
																}
																onChange={(data) => {
																	const truncatedAddress =
																		truncateAddressToCity(
																			data.address,
																			data.city || ''
																		);
																	setFieldValue(
																		'destination_address',
																		truncatedAddress
																	);
																	setFieldValue(
																		'end_location_pincode',
																		data.pincode ||
																			values.end_location_pincode
																	);
																	setNodeSequence((prev) => {
																		const u = [...prev];
																		if (u.length > 0)
																			u[u.length - 1] = {
																				...u[u.length - 1],
																				address:
																					truncatedAddress,
																			};
																		return u;
																	});
																	setTimeout(
																		() => validateForm(),
																		0
																	);
																}}
																placeholder="Select Destination"
																disabled={
																	isEditMode ||
																	!values.route_type ||
																	values.route_type ===
																		'round_trip'
																}
															/>
														</FormInput>
													</RouteFieldWrapper>
												</RouteFieldsRow>
												<SelectNodeLink
													type="button"
													onClick={() => {
														if (
															(isEditMode && !isFormEditable) ||
															!values.route_type
														) {
															return;
														}
														setNodeSequenceModalVisible(true);
													}}
													disabled={isEditMode || !values.route_type}
												>
													Select Node Sequence
												</SelectNodeLink>
											</RouteContainer>
										</CompactCol>
									</CompactRow>
									<NodeSequenceModal
										visible={nodeSequenceModalVisible}
										onClose={() => setNodeSequenceModalVisible(false)}
										routeType={values.route_type}
										onConfirm={(confirmedNodes) => {
											setNodeSequence(confirmedNodes);
											if (confirmedNodes.length > 0) {
												setFieldValue(
													'start_address',
													confirmedNodes[0].address || ''
												);
											}
											if (confirmedNodes.length > 1) {
												setFieldValue(
													'destination_address',
													confirmedNodes[confirmedNodes.length - 1]
														.address || ''
												);
											}
											const intermediary = confirmedNodes
												.filter(
													(n) => n.type === 'intermediary' && n.address
												)
												.map((n) => n.address);
											setFieldValue('node_sequence', confirmedNodes);
											setFieldValue('intermediary_addresses', intermediary);
											setNodeSequenceModalVisible(false);
										}}
										initialNodes={
											nodeSequence.length > 0
												? nodeSequence
												: [
														{
															address: values.start_address || '',
															type: 'start',
														},
														{ address: '', type: 'intermediary' },
														{
															address:
																values.destination_address || '',
															type: 'end',
														},
												  ]
										}
										disabled={isEditMode || !values.route_type}
									/>
								</>
							)}
							<CompactRow gutter={16}>
								{values.trip_type === 'adhoc' && (
									<CompactCol span={12}>
										<FormInput name="vendor_type" label="Vendor Type *">
											<Select
												name="vendor_type"
												showSearch
												options={vendorUserType}
												placeholder="Select"
												disabled={
													isEditMode ||
													(isV2UI && values.trip_type !== 'adhoc')
												}
												onChange={(e) =>
													onVendorTypeChange(e, setFieldValue)
												}
											/>
										</FormInput>
									</CompactCol>
								)}
								<CompactCol span={12}>
									<FormInput name="vendor_id" label="Select Vendor *">
										<Select
											name="vendor_id"
											showSearch
											options={vendorsData}
											placeholder="Select"
											disabled={
												isEditMode ||
												(isV2UI && values.trip_type !== 'adhoc')
											}
											onChange={(e) =>
												onVendorChange(e, setFieldValue, values)
											}
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
										/>
									</FormInput>
								</CompactCol>
							</CompactRow>
							{values.trip_type === 'adhoc' && (
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput name="vendor_rate" label="Vendor Rate *">
											<CustomInput
												name="vendor_rate"
												placeholder="Vendor Rate"
												type="number"
												disabled={isEditMode}
											/>
										</FormInput>
									</CompactCol>
									<CompactCol span={12}>
										<FormInput name="client_rate" label="Client Rate *">
											<CustomInput
												name="client_rate"
												placeholder="Client Rate"
												type="number"
												disabled={isEditMode}
											/>
										</FormInput>
									</CompactCol>
								</CompactRow>
							)}
							<CompactRow gutter={16}>
								<CompactCol span={12}>
									<FormInput name="vehicle_type" label="Vehicle Type *">
										<Select
											name="vehicle_type"
											showSearch
											options={vehicleTypes}
											placeholder="Select"
											disabled={
												isEditMode ||
												(isV2UI && values.trip_type !== 'adhoc')
											}
											onChange={(e) =>
												onVehicleTypeChange(e, setFieldValue, values)
											}
										/>
									</FormInput>
								</CompactCol>
								<CompactCol span={12}>
									{isV2UI ? (
										<FormInput name="vehicle_number" label="Vehicle No *">
											<Select
												name="vehicle_number"
												showSearch
												options={vehicleData}
												placeholder="Select"
												disabled={
													isEditMode && !canSelectCrossVendorVehicle
												}
												onChange={(e) =>
													onVehicleNumberChange(e, setFieldValue, values)
												}
												filterOption={(input, option) =>
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
												dropdownRender={
													canSelectCrossVendorVehicle &&
													values.trip_type === 'adhoc'
														? // eslint-disable-next-line react/no-unstable-nested-components
														  (menu) => (
																<>
																	{menu}
																	<div
																		role="button"
																		tabIndex={0}
																		onMouseDown={(ev) => {
																			ev.preventDefault();
																			setCrossVendorVisible(
																				true
																			);
																		}}
																		style={{
																			padding: '8px 12px',
																			color: '#18405D',
																			cursor: 'pointer',
																			fontWeight: 500,
																			textDecoration:
																				'underline',
																			borderTop:
																				'1px solid #f0f0f0',
																		}}
																	>
																		Select from different vendor
																	</div>
																</>
														  )
														: undefined
												}
											/>
										</FormInput>
									) : (
										<FormInput name="vehicle_number" label="Vehicle No *">
											<CustomInput
												name="vehicle_number"
												placeholder="Vehicle Number"
												isCaps
											/>
										</FormInput>
									)}
									{canReplaceVehicle && (
										<ReplaceLinkRow>
											<ReplaceLink
												type="button"
												disabled={!isFormEditable}
												onClick={() => {
													if (!isFormEditable) return;
													setReplaceVehicleVisible(true);
												}}
											>
												<RetweetOutlined />
												Replace Vehicle
											</ReplaceLink>
										</ReplaceLinkRow>
									)}
								</CompactCol>
							</CompactRow>
							<CompactRow gutter={16}>
								{(isV2UI || values.trip_type === 'regular') && (
									<CompactCol span={12}>
										<FormInput name="driver_id" label="Driver *">
											<Select
												name="driver_id"
												showSearch
												options={
													values.driver_id &&
													!(driverData || []).some(
														(o) => o.value === values.driver_id
													)
														? [
																...(driverData || []),
																{
																	value: values.driver_id,
																	label:
																		values.driver_name ||
																		String(values.driver_id),
																},
														  ]
														: driverData
												}
												placeholder="Select"
												disabled={
													!canAssignDriverForLaterStatus &&
													((!isV2UI && isEditMode) ||
														(isV2UI && isEditMode && !isFormEditable) ||
														(isV2UI &&
															isEditMode &&
															isFormEditable &&
															!isDriverStatusEditable &&
															!canSelectCrossVendorVehicle))
												}
												onChange={(e) =>
													onDriverChange(e, setFieldValue, values)
												}
												filterOption={(input, option) =>
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
											/>
										</FormInput>
										{isEditMode && isV2UI && hasReplaceDriverPermission && (
											<ReplaceLinkRow>
												<ReplaceLink
													type="button"
													disabled={!isFormEditable || !canReplaceDriver}
													title={
														!canReplaceDriver
															? 'Driver can be replaced only after the trip has started'
															: undefined
													}
													onClick={() => {
														if (!isFormEditable || !canReplaceDriver)
															return;
														setReplaceDriverVisible(true);
													}}
												>
													<RetweetOutlined />
													Replace Driver
												</ReplaceLink>
											</ReplaceLinkRow>
										)}
									</CompactCol>
								)}
								{isV2UI ? (
									<CompactCol span={12}>
										<FormInput name="driver_number" label="Driver Number">
											<CustomInput
												name="driver_number"
												placeholder="Driver Number"
												disabled={
													(!isV2UI && isEditMode) ||
													(isV2UI && isEditMode && !isFormEditable) ||
													(isV2UI &&
														isEditMode &&
														isFormEditable &&
														!(
															!values.driver_id ||
															values.driver_id === ''
														))
												}
											/>
										</FormInput>
									</CompactCol>
								) : values.trip_type !== 'regular' ? (
									<>
										<CompactCol span={12}>
											<FormInput name="driver_name" label="Driver Name *">
												<CustomInput
													name="driver_name"
													placeholder="Driver Name"
												/>
											</FormInput>
										</CompactCol>
										<CompactCol span={12}>
											<FormInput name="driver_number" label="Driver Number *">
												<CustomInput
													name="driver_number"
													placeholder="Driver Number"
													maxLength={10}
													value={values.driver_number}
													onChange={(e) => {
														const { value } = e.target;
														const numericValue = value.replace(
															/[^0-9]/g,
															''
														);
														if (numericValue.length > 0) {
															const firstDigit = parseInt(
																numericValue[0],
																10
															);
															if (
																![6, 7, 8, 9].includes(firstDigit)
															) {
																return;
															}
														}
														if (numericValue.length <= 10) {
															setFieldValue(
																'driver_number',
																numericValue
															);
														}
													}}
													onKeyPress={(e) => {
														const isNumber = /[0-9]/.test(e.key);
														const isControlKey = [
															'Backspace',
															'Delete',
															'ArrowLeft',
															'ArrowRight',
															'Tab',
															'Enter',
														].includes(e.key);
														if (isNumber && !e.target.value) {
															const digit = parseInt(e.key, 10);
															if (![6, 7, 8, 9].includes(digit)) {
																e.preventDefault();
																return;
															}
														}

														if (!isNumber && !isControlKey) {
															e.preventDefault();
														}
													}}
													disabled={isEditMode}
												/>
											</FormInput>
										</CompactCol>
									</>
								) : null}
							</CompactRow>
							<CompactRow gutter={16}>
								<CompactCol span={12}>
									<FormInput name="reporting_date" label="Reporting Date *">
										<DatePickers
											name="reporting_date"
											showTime
											format="MM/DD/YYYY h:mm A"
											disabledDate={(current) =>
												current && current > moment().endOf('day')
											}
										/>
									</FormInput>
								</CompactCol>
								<CompactCol span={12}>
									<FormInput name="start_date" label="Start Date *">
										<DatePickers
											name="start_date"
											showTime
											format="MM/DD/YYYY h:mm A"
											disabledDate={(current) =>
												current && current > moment().endOf('day')
											}
										/>
									</FormInput>
								</CompactCol>
							</CompactRow>
							<CompactRow gutter={16}>
								<CompactCol span={12}>
									<FormInput name="start_km" label="Start KM *">
										<CustomInput
											name="start_km"
											placeholder="Start KM"
											type="number"
										/>
									</FormInput>
								</CompactCol>
								<CompactCol span={12}>
									<div style={{ fontWeight: 500, marginBottom: 8 }}>
										Upload odometer Start (Optional)
									</div>
									<UploadWithPreview
										beforeUpload={async (file) =>
											onOdometerUpload(file, setFieldValue)
										}
										uploading={uploadingOdometer}
										uploadError={uploadErrorOdometer}
										uploadedFile={
											values.odometer_start &&
											values.odometer_start.length > 0
												? values.odometer_start[0]
												: null
										}
										onFileRemove={() => setFieldValue('odometer_start', [])}
										buttonText="Upload"
										accept={['.pdf', '.jpg', '.jpeg', '.png']}
										entity="trip"
									/>
								</CompactCol>
							</CompactRow>
							{values.trip_type === 'adhoc' && (
								<>
									<CompactRow gutter={16}>
										<CompactCol span={12}>
											<div style={{ fontWeight: 500, marginBottom: 8 }}>
												Upload Proof of Request (mandatory while creating
												trip)
											</div>
											<UploadWithPreview
												beforeUpload={async (file) =>
													onProofUpload(file, setFieldValue)
												}
												uploading={uploadingProof}
												disabled={isEditMode && !isFormEditable}
												uploadError={uploadErrorProof}
												uploadedFile={
													values.requirement_proof &&
													values.requirement_proof.length > 0
														? values.requirement_proof[0]
														: null
												}
												onFileRemove={() =>
													setFieldValue('requirement_proof', [])
												}
												hideRemove={
													isEditMode &&
													formData?.is_approved === true &&
													formData?.por_approval_status !== 'rejected'
												}
												buttonText="Upload Proof"
												accept={['.pdf', '.jpg', '.jpeg', '.png']}
												entity="trip"
											/>
										</CompactCol>
										<CompactCol span={12}>
											{/** keep space aligned or show helper error */}
											{values.requirement_proof && (
												<div style={{ visibility: 'hidden' }}>.</div>
											)}
										</CompactCol>
									</CompactRow>
									<CompactRow gutter={16}>
										<CompactCol span={12}>
											{/** show validation error explicitly to match intercity UX */}
											{/* eslint-disable-next-line react/prop-types */}
											<FormInput name="requirement_proof">
												{/* placeholder wrapper to render error below uploader */}
												<div />
											</FormInput>
										</CompactCol>
									</CompactRow>
								</>
							)}
							{values.trip_type === 'adhoc' && (
								<CompactCol gutter={16}>
									<FormInput name="remarks" label="Remarks">
										<>
											<Input.TextArea
												name="remarks"
												placeholder="Enter remarks"
												maxLength={250}
												rows={4}
												style={{ resize: 'none' }}
											/>
											<div
												style={{
													textAlign: 'right',
													fontSize: 12,
													color: '#6b7280',
													marginTop: 4,
												}}
											>
												{values.remarks?.length || 0}/250
											</div>
										</>
									</FormInput>
								</CompactCol>
							)}
							<FormButtonContainer>
								<FormCancelButton
									type="button"
									onClick={() => navigate('/trips/intracity')}
								>
									Cancel
								</FormCancelButton>
								<FormPrimaryButton
									type="submit"
									disabled={
										isSubmitting ||
										(isEditMode && !isFormEditable) ||
										formData?.isEnded
									}
									loading={isSubmitting}
								>
									{isEditMode ? 'Submit' : 'Create'}
								</FormPrimaryButton>
							</FormButtonContainer>
						</Form>
						{canSelectCrossVendorVehicle && values.trip_type === 'adhoc' && (
							<VehicleSelectionFromAnotherVendorModal
								visible={crossVendorVisible}
								billingVendorId={values.vendor_id}
								onClose={() => setCrossVendorVisible(false)}
								onSelect={(payload) => {
									if (onCrossVendorSelect) {
										onCrossVendorSelect(payload, setFieldValue, values);
									}
									setCrossVendorVisible(false);
								}}
							/>
						)}
						{(canReplaceVehicle || canReplaceDriver) && (
							<>
								<ReplaceVehicleModal
									mode="trip"
									visible={replaceVehicleVisible}
									tripData={{
										trip_id: formData?.id,
										vendor_id: values.vendor_id,
										vendor_name: formData?.vendor_name,
										vehicle_number: values.vehicle_number,
										vehicle_type: values.vehicle_type,
										vehicle_type_id: vehicleTypeIdMap?.[values.vehicle_type],
										driver_id: values.driver_id,
										driver_name: values.driver_name,
										current_cost: values.vendor_rate,
										vendor_rate_basis: formData?.vendor_rate_basis,
										billing_type: formData?.billing_type,
										billing_subtype: formData?.billing_subtype,
										current_vendor_rate: formData?.current_vendor_rate,
										current_charges_per_km: formData?.current_charges_per_km,
										order_rates: formData?.order_rates,
									}}
									onConfirm={(payload) => {
										if (payload.new_vehicle_number) {
											setFieldValue(
												'vehicle_number',
												payload.new_vehicle_number
											);
										}
										if (payload.new_driver_id) {
											setFieldValue('driver_id', payload.new_driver_id);
											setFieldValue(
												'driver_name',
												payload.new_driver_name || ''
											);
											setFieldValue(
												'driver_number',
												payload.new_driver_number || ''
											);
										}
										message.success('Vehicle replaced successfully');
										setReplaceVehicleVisible(false);
										setTimeout(() => window.location.reload(), 800);
										return true;
									}}
									onClose={() => setReplaceVehicleVisible(false)}
								/>
								<ReplaceDriverModal
									visible={replaceDriverVisible}
									tripData={{
										trip_id: formData?.id,
										vendor_id: values.vendor_id,
										vendor_name: formData?.vendor_name,
										vehicle_type: values.vehicle_type,
										vehicle_type_id: vehicleTypeIdMap?.[values.vehicle_type],
										driver_id: values.driver_id,
										driver_name: values.driver_name,
									}}
									onConfirm={(payload) => {
										setFieldValue('driver_id', payload.new_driver_id);
										setFieldValue('driver_name', payload.new_driver_name || '');
										setFieldValue(
											'driver_number',
											payload.new_driver_number || ''
										);
										message.success('Driver replaced successfully');
										setReplaceDriverVisible(false);
										// Reload so dependent fields re-fetch fresh.
										setTimeout(() => window.location.reload(), 800);
										return true;
									}}
									onClose={() => setReplaceDriverVisible(false)}
								/>
							</>
						)}
					</>
				)}
			</Formik>
		</FormContainer>
	);
}

IntracityTripForm.propTypes = {
	isEditMode: PropTypes.bool,
	formData: PropTypes.object,
	cities: PropTypes.array,
	projectData: PropTypes.array,
	vendorsData: PropTypes.array,
	vehicleTypes: PropTypes.array,
	vehicleTypeIdMap: PropTypes.object,
	vehicleData: PropTypes.array,
	driverData: PropTypes.array,
	routeData: PropTypes.array, // Routes for v2 API
	isV2UI: PropTypes.bool, // Flag to show v2-only fields
	uploadingOdometer: PropTypes.bool,
	uploadErrorOdometer: PropTypes.string,
	uploadingProof: PropTypes.bool,
	uploadErrorProof: PropTypes.string,
	vendorUserType: PropTypes.array,
	formRef: PropTypes.any,
	onCityChange: PropTypes.func,
	onProjectChange: PropTypes.func,
	onVendorTypeChange: PropTypes.func,
	onVehicleTypeChange: PropTypes.func,
	onVendorChange: PropTypes.func,
	onTripTypeChange: PropTypes.func,
	onRouteChange: PropTypes.func,
	onVehicleNumberChange: PropTypes.func,
	clientAllowsCrossVendor: PropTypes.bool,
	onCrossVendorSelect: PropTypes.func,
	onDriverChange: PropTypes.func,
	onProofUpload: PropTypes.func,
	onOdometerUpload: PropTypes.func,
	onSubmit: PropTypes.func,
	navigate: PropTypes.func,
};
